import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { triggersQuery } from 'components/domain/automation/triggers/triggersQuery';
import useFilterFormContext from 'components/filters/useFilterFormContext';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { Trigger } from 'models/automation/Triggers';
import { DEFAULT_AUTOMATIONS_PAGE_SIZE } from 'pages/automations/Automations.constants';
import { TriggersTableStyled } from '../TriggersTab.styles';
import { TriggersTabFiltersSchemaType } from '../filters/TriggersTabFilters.schema';
import useSearchedTrigger from '../filters/useSearchedTrigger';
import ActionTypeCell from './cells/ActionTypeCell';
import ActionsCell from './cells/ActionsCell';
import RecipientsCell from './cells/RecipientsCell';
import StatusToggleCell from './cells/StatusToggleCell';
import TriggerTypeCell from './cells/TriggerTypeCell';

const TriggersTable = () => {
  const { t } = useTranslation();
  const searchTerm = useSearchedTrigger();
  const { lastSubmittedFilters } =
    useFilterFormContext<TriggersTabFiltersSchemaType>();
  const columnHelper = createColumnHelper<Trigger>();

  const columns = [
    columnHelper.accessor('name', {
      header: t('common.name'),
      sortDescFirst: true,
    }),
    columnHelper.accessor('triggerType', {
      header: t('pageAutomations.triggers.table.triggerType'),
      cell: TriggerTypeCell,
      enableSorting: false,
    }),
    columnHelper.accessor('actionType', {
      header: t('pageAutomations.triggers.table.actionType'),
      cell: ActionTypeCell,
      enableSorting: false,
    }),
    columnHelper.accessor('recipients', {
      header: t('pageAutomations.triggers.table.recipients'),
      cell: RecipientsCell,
      enableSorting: false,
    }),
    columnHelper.accessor('templateName', {
      header: t('pageAutomations.triggers.table.template'),
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('isActive', {
      id: 'status',
      header: t('common.status'),
      cell: StatusToggleCell,
      enableSorting: false,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { tableInstance, isLoading } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      triggersQuery({
        filters: { ...lastSubmittedFilters },
        searchTerm,
        ...options,
      }),
    pageSize: DEFAULT_AUTOMATIONS_PAGE_SIZE,
  });

  return (
    <TriggersTableStyled
      data-testid="triggers-table"
      emptyState={t('pageAutomations.triggers.emptyTable')}
      isLoading={isLoading}
      rightOffset={160}
      tableInstance={tableInstance}
    />
  );
};

export default TriggersTable;
